import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle, Info, X, XCircle } from 'lucide-react';
import { subscribeToToasts } from '../services/toastBus';
import './ToastHost.css';

const AUTO_DISMISS_MS = 5000;

const ICONS = {
  success: CheckCircle,
  error: XCircle,
  warning: AlertTriangle,
  info: Info,
};

/**
 * Errors and warnings interrupt; confirmations wait their turn.
 *
 * `role="alert"` carries an implicit `aria-live="assertive"` and `role="status"` an
 * implicit `polite`, so no redundant `aria-live` attribute is needed. Announcing
 * everything assertively would make routine confirmations cut across whatever a
 * screen-reader user is currently reading.
 */
const isUrgent = (type) => type === 'error' || type === 'warning';

/**
 * The single toast host. Mounted once in App.js OUTSIDE both layouts — `Layout`
 * unmounts on any navigation into `BareLayout` (/select-agency, /agency-suspended)
 * or /login, and a host that unmounts takes its queue with it.
 *
 * Renders nothing at all while there is nothing to say.
 */
function ToastHost() {
  const [toasts, setToasts] = useState([]);
  const timers = useRef(new Map());

  const dismiss = useCallback((id) => {
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  useEffect(() => {
    // Read the ref once: the Map's identity is stable for the component's whole
    // lifetime (only its contents change), so capturing it here is both what the
    // exhaustive-deps rule wants for the cleanup path and genuinely equivalent.
    const pending = timers.current;
    const unsubscribe = subscribeToToasts((toast) => {
      setToasts((current) => [...current, toast]);
      pending.set(toast.id, setTimeout(() => dismiss(toast.id), AUTO_DISMISS_MS));
    });
    return () => {
      unsubscribe();
      pending.forEach((timer) => clearTimeout(timer));
      pending.clear();
    };
  }, [dismiss]);

  if (toasts.length === 0) return null;

  return (
    <div className="toast-host">
      {toasts.map((toast) => {
        const Icon = ICONS[toast.type] ?? Info;
        return (
          <div
            key={toast.id}
            className={`toast toast-${toast.type}`}
            role={isUrgent(toast.type) ? 'alert' : 'status'}
          >
            <Icon className="toast-icon" size={18} aria-hidden="true" />
            <p className="toast-message">{toast.message}</p>
            <button
              type="button"
              className="btn btn-inherit btn-icon btn-sm toast-dismiss"
              onClick={() => dismiss(toast.id)}
              // Includes the message: stacked toasts otherwise all announce the
              // same name, so tabbing straight to one gives no clue which it is.
              aria-label={`Dismiss notification: ${toast.message}`}
            >
              <X size={16} aria-hidden="true" />
            </button>
          </div>
        );
      })}
    </div>
  );
}

export default ToastHost;
